import React, { useEffect } from 'react'

import { TabMenu } from '@amusnet/react-ui'
import {
    useBuyBonusStore,
    useConfigurationStore,
    useDoubleChanceStore,
    useMenuStore,
    useNavigationStore,
    usePlayerInfoStore,
} from '@stores'
import { useMenuTabs } from './useMenuTabs.tsx'
import { useFormatMoney } from '@hooks'
import { MenuSectionId, MenuSectionsIds } from 'game-ui-types'
import { useTranslation } from '@amusnet/translations/react'

import {
    GameUIMessageScope,
    GameUIMessageType,
    MenuNavigationBarTappedMessage,
    SetSectionIDMenuMessage,
    ViewportType,
} from 'game-ui-types'
import useGetBetAmount from '@hooks/useGetBetAmount'
import { useSoundStore } from '@stores/soundStore'
import { SOUNDS } from '@constants/sounds'
import { cn } from '@amusnet/react-ui'
import { gameAnalytics } from '@utils/gameAnalytics'

const Menu: React.FC = () => {
    const { t } = useTranslation()

    const setIsMenuOpen = useNavigationStore((state) => state.setIsMenuOpen)
    const viewport = useConfigurationStore((state) => state.viewport)
    const currency = useConfigurationStore((state) => state.currency)
    const shouldBalanceSubtractOnly = useConfigurationStore((state) => state.shouldBalanceSubtractOnly)
    const balance = usePlayerInfoStore((state) => state.balance)
    const subtractedBalance = usePlayerInfoStore((state) => state.subtractedBalance)
    const setSelectedTabIndex = useMenuStore((state) => state.setSelectedTabIndex)
    const currentSelectedTabIndex = useMenuStore((state) => state.currentSelectedTabIndex)
    const formatMoney = useFormatMoney()
    const shouldOpenDefaultSection = useMenuStore((state) => state.shouldOpenDefaultSection)
    const setShouldOpenDefaultSection = useMenuStore((state) => state.setShouldOpenDefaultSection)
    const targetMenuSectionId = useMenuStore((state) => state.targetMenuSectionId)
    const setTargetMenuSectionId = useMenuStore((state) => state.setTargetMenuSectionId)
    const defaultMenuSectionId = useConfigurationStore((state) => state.defaultMenuSectionId)
    const playEngineSound = useSoundStore((state) => state.playEngineSound)
    const sendTotalsInfo = useConfigurationStore((state) => state.sendTotalsInfo)
    const hasBuyBonus = useConfigurationStore((state) => state.hasBuyBonus)
    const hasDoubleChance = useConfigurationStore((state) => state.hasDoubleChance)
    const isBuyBonusActive = useBuyBonusStore((state) => state.isMultiChoiceToggled) && hasBuyBonus
    const isDoubleChanceActive = useDoubleChanceStore((state) => state.isDoubleChanceActivated) && hasDoubleChance
    const isAutoplaySettingsTabInactive = isBuyBonusActive || isDoubleChanceActive

    const betAmount = useGetBetAmount()
    const displayedBalance = shouldBalanceSubtractOnly ? (subtractedBalance ?? balance) : balance

    const marginBottomMap: Partial<Record<ViewportType, string>> = {
        [ViewportType.MOBILE_LANDSCAPE]: 'mb-[15px]',
        [ViewportType.MOBILE_PORTRAIT]: sendTotalsInfo ? 'mb-[33px]' : 'mb-[19px]',
    }

    const tabs = useMenuTabs()

    let currentIndex = currentSelectedTabIndex

    const getIndexById = (id: MenuSectionId) => {
        const result = tabs.findIndex((section) => {
            return section.key === id
        })

        return result !== -1 ? result : 0
    }

    if (targetMenuSectionId) {
        const targetIndex = tabs.findIndex((section) => section.key === targetMenuSectionId)

        if (
            targetIndex !== -1 &&
            !(targetMenuSectionId === MenuSectionsIds.AUTOPLAY_SETTINGS && isAutoplaySettingsTabInactive)
        ) {
            currentIndex = targetIndex
        }

        setTargetMenuSectionId(null)
    } else if (shouldOpenDefaultSection) {
        const defaultIndex = tabs.findIndex((section) => {
            return section.key === defaultMenuSectionId
        })

        if (defaultIndex != -1) {
            currentIndex = defaultIndex
            setSelectedTabIndex(defaultIndex)
        }

        setShouldOpenDefaultSection(false)
    }

    const marginBottom = marginBottomMap[viewport] ?? ''

    if (tabs[currentIndex]?.key === MenuSectionsIds.AUTOPLAY_SETTINGS && isAutoplaySettingsTabInactive) {
        currentIndex = getIndexById(MenuSectionsIds.GAME_SETTINGS)
        setSelectedTabIndex(currentIndex)
    }

    const activeTabKey = tabs[currentIndex]?.key

    // Fires both when the menu opens on a section and when the player switches to one, which is what
    // "section opened" means for reporting.
    useEffect(() => {
        if (activeTabKey === MenuSectionsIds.GAME_SETTINGS) gameAnalytics.gameSettingsOpened()

        if (activeTabKey === MenuSectionsIds.INFORMATION) gameAnalytics.infoPageOpened()

        if (activeTabKey === MenuSectionsIds.HISTORY) gameAnalytics.historyOpened()
    }, [activeTabKey])

    const onSelectedIndexChanged = (index: number) => {
        if (tabs[index]?.key === MenuSectionsIds.AUTOPLAY_SETTINGS && isAutoplaySettingsTabInactive) {
            return
        }

        setSelectedTabIndex(index)

        const doubleChanceClickMessage: MenuNavigationBarTappedMessage = {
            scope: GameUIMessageScope.MENU,
            type: GameUIMessageType.MENU_NAVIGATION_BAR_TAPPED,
            payload: { id: tabs[index].key },
        }

        window.uiBus.postMessage(doubleChanceClickMessage)
    }

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const type = rawData.type

            if (rawData.scope !== GameUIMessageScope.MENU) {
                return
            }

            switch (type) {
                case GameUIMessageType.SET_MENU_SECTION_ID: {
                    const data = rawData as SetSectionIDMenuMessage

                    if (data.payload.id === MenuSectionsIds.AUTOPLAY_SETTINGS && isAutoplaySettingsTabInactive) {
                        break
                    }

                    setSelectedTabIndex(getIndexById(data.payload.id))
                    break
                }
            }
        }

        window.uiBus.addEventListener('message', handleMessage, false)

        return () => {
            window.uiBus.removeEventListener('message', handleMessage, false)
        }
    })

    return (
        <div className={cn(isAutoplaySettingsTabInactive && 'menu-autoplay-tab-inactive')}>
            <TabMenu
                onCloseClick={() => {
                    setIsMenuOpen(false)
                    playEngineSound(SOUNDS.HIDE_DENOMINATIONS)
                }}
                balance={formatMoney(displayedBalance)}
                iconContainerClassName={marginBottom}
                betAmount={formatMoney(betAmount)}
                currency={currency}
                layout={viewport}
                tabs={tabs}
                currentIndex={currentIndex}
                onSelectedIndexChanged={onSelectedIndexChanged}
                betLabel={t('common.bet')}
                balanceLabel={t('common.balance')}
            />
        </div>
    )
}

export default Menu
